import React, { useEffect, useState, useCallback } from "react";
import { observer } from "mobx-react-lite";
import {
  CContainer,
  CCard,
  CCardBody,
  CFormSelect,
  CTable,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableBody,
  CTableDataCell,
} from "@coreui/react";
import settingsStore from "../../store/SettingsStore";
import periodPrefsStore from "../../store/PeriodPrefsStore";
import PeriodFilters from "../UI/PeriodFilters/PeriodFilters";
import ServerSelect from "../UI/PeriodFilters/ServerSelect";
import ErrorAlert from "../UI/Status/ErrorAlert";
import { SkeletonCard } from "../UI/Skeleton/Skeleton";
import { getCustomReportPresets, runCustomReport } from "../../api/customReportsApi";
import { fmtInt } from "../../utils/chartHelpers";
import { logReportView } from "../../api/reportLogApi";
import { formatYmd } from "../../utils/dateRange";

function formatCell(value, type) {
  if (value == null || value === "") return "—";
  switch (type) {
    case "MONEY":
      return `${fmtInt(value)} ₽`;
    case "PERCENT":
      return `${(Number(value) * 100).toFixed(1)}%`;
    case "AMOUNT":
    case "INTEGER":
      return fmtInt(value);
    default:
      return String(value);
  }
}

const CustomReportsPage = observer(() => {
  const [selectedServer, setSelectedServer] = useState(periodPrefsStore.selectedServer);
  const [fromDate, setFromDate] = useState(periodPrefsStore.fromDate);
  const [toDate, setToDate] = useState(periodPrefsStore.toDate);
  const [quickRange, setQuickRange] = useState(periodPrefsStore.quickRange);

  const [presets, setPresets] = useState([]);
  const [presetsLoading, setPresetsLoading] = useState(false);
  const [presetsError, setPresetsError] = useState(null);
  const [selectedPresetId, setSelectedPresetId] = useState("");

  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadPresets = useCallback(() => {
    if (!selectedServer) return;
    setPresetsLoading(true);
    setPresetsError(null);
    setPresets([]);
    setSelectedPresetId("");
    setResult(null);
    getCustomReportPresets(selectedServer)
      .then((data) => {
        const list = data?.presets || [];
        setPresets(list);
        if (list.length) setSelectedPresetId(list[0].id);
      })
      .catch((e) => setPresetsError(e))
      .finally(() => setPresetsLoading(false));
  }, [selectedServer]);

  useEffect(() => {
    const init = async () => {
      const userId = localStorage.getItem("userId");
      if (userId) await settingsStore.getServers(userId);
      if (!fromDate || !toDate) {
        const today = new Date();
        const past = new Date(today);
        past.setDate(today.getDate() - 7);
        setFromDate(formatYmd(past));
        setToDate(formatYmd(today));
      }
      logReportView({ event: "report_view", reportKey: "custom_reports", reportTitle: "Отчёты iiko", userId, path: "/custom-reports" });
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!settingsStore.servers?.length) return;
    if (!selectedServer) setSelectedServer(String(settingsStore.servers[0].id));
  }, [settingsStore.servers, selectedServer]);

  useEffect(() => {
    if (selectedServer) periodPrefsStore.setServer(selectedServer);
  }, [selectedServer]);

  useEffect(() => {
    loadPresets();
  }, [loadPresets]);

  const runReport = useCallback(
    (from = fromDate, to = toDate) => {
      if (!selectedServer || !selectedPresetId || !from || !to) return;
      setLoading(true);
      setError(null);
      runCustomReport(selectedServer, selectedPresetId, from, to)
        .then(setResult)
        .catch((e) => setError(e))
        .finally(() => setLoading(false));
    },
    [selectedServer, selectedPresetId, fromDate, toDate],
  );

  const handleRefresh = (from, to) => {
    periodPrefsStore.setPeriod(from, to, quickRange);
    runReport(from, to);
  };

  return (
    <CContainer style={{ flex: "1", marginTop: "120px" }}>
      <h4 className="mb-3">Отчёты iiko</h4>
      <p className="text-body-secondary small">
        Пользовательские отчёты, уже настроенные в конструкторе iikoOffice — выберите один и запустите за нужный
        период. Показаны только отчёты, у которых в iiko задан фильтр по дате (им безопасно подставить актуальный
        период); отчёты без привязки к дате не показываются — их нельзя ограничить по времени.
      </p>

      <ServerSelect servers={settingsStore.servers} value={selectedServer} onChange={(e) => setSelectedServer(e.target.value)} />

      {presetsLoading && <SkeletonCard lines={1} />}
      <ErrorAlert error={presetsError} />
      {!presetsLoading && !presetsError && presets.length === 0 && (
        <div className="text-body-secondary small mb-3">
          На этом сервере нет пользовательских отчётов с привязкой к периоду.
        </div>
      )}
      {!presetsLoading && presets.length > 0 && (
        <CFormSelect
          className="mb-3"
          style={{ maxWidth: 480 }}
          value={selectedPresetId}
          onChange={(e) => setSelectedPresetId(e.target.value)}
        >
          {presets.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </CFormSelect>
      )}

      <PeriodFilters
        fromDate={fromDate}
        toDate={toDate}
        onFromChange={setFromDate}
        onToChange={setToDate}
        onRefresh={handleRefresh}
        quickRange={quickRange}
        onQuickRangeChange={setQuickRange}
        disabled={loading || !selectedPresetId}
        loading={loading}
      />

      <ErrorAlert error={error} />

      {result && (
        <CCard className="mt-3">
          <CCardBody>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="fw-semibold">{result.report_name}</span>
              <span className="small text-body-secondary">
                {result.date_from} — {result.date_to}
              </span>
            </div>
            {result.date_field_updated && (
              <div className="small text-body-secondary mb-2">
                Поле даты этого отчёта устарело в iiko — автоматически использовано актуальное.
              </div>
            )}
            {result.rows.length === 0 ? (
              <div className="text-body-secondary small">Нет данных за период.</div>
            ) : (
              <>
                <CTable small hover responsive>
                  <CTableHead>
                    <CTableRow>
                      {result.columns.map((c) => (
                        <CTableHeaderCell key={c.field}>{c.label}</CTableHeaderCell>
                      ))}
                    </CTableRow>
                  </CTableHead>
                  <CTableBody>
                    {result.rows.map((row, i) => (
                      <CTableRow key={i}>
                        {result.columns.map((c) => (
                          <CTableDataCell key={c.field}>{formatCell(row[c.field], c.type)}</CTableDataCell>
                        ))}
                      </CTableRow>
                    ))}
                  </CTableBody>
                </CTable>
                <div className="small text-body-secondary mt-2">
                  {result.truncated
                    ? `Показаны первые ${result.rows.length} из ${result.total_rows} строк — сузьте период или фильтры в самом отчёте.`
                    : `Строк: ${result.total_rows}`}
                </div>
              </>
            )}
          </CCardBody>
        </CCard>
      )}
    </CContainer>
  );
});

export default CustomReportsPage;
